/**
 * Deliberately dependency-free: the app reducer reads this at module-init time, and
 * importing V4client there closes a cycle (V4client -> BaseAPI -> configureStore -> reducers).
 */

const V4_USER_KEY = 'v4User';
const V4_SCOPE_KEY = 'v4ScopedAccount';

/** Routes with their own public / token-based login flow. */
export const V4_SESSION_EXCLUDED_PATHS = [
  '/v4/',
  '/auth/',
  '/presentation/',
  '/live-location/',
  '/location-alarm/',
  '/alarm-location/', // tryLoginPublicViaToken in alarm.saga
  '/alarm/',
  '/newPassword',
  '/forgotPassword',
];

export const isOnV4ExcludedPath = () => {
  const path = window?.location?.pathname || '';
  return V4_SESSION_EXCLUDED_PATHS.some(p => path.startsWith(p));
};

const readV4User = () => {
  try {
    return JSON.parse(localStorage.getItem(V4_USER_KEY) || sessionStorage.getItem(V4_USER_KEY));
  } catch (_) {
    return null;
  }
};

export const getV4Token = () => readV4User()?.token || null;

export const getV4RefreshToken = () => readV4User()?.refreshToken || null;

// Back where getV4Token reads it; a cleared session stays cleared.
export function storeV4Token(token, refreshToken) {
  try {
    const storage = localStorage.getItem(V4_USER_KEY) ? localStorage : sessionStorage;
    const v4User = JSON.parse(storage.getItem(V4_USER_KEY));
    if (v4User) {
      storage.setItem(V4_USER_KEY, JSON.stringify({...v4User, token, ...(refreshToken ? {refreshToken} : {})}));
    }
  } catch (_) {}
}

export function getV4SessionScope() {
  try {
    return JSON.parse(localStorage.getItem(V4_SCOPE_KEY));
  } catch (_) {
    return null;
  }
}

export function setV4SessionScope(accountId, accountName, {manager = false, trail = []} = {}) {
  localStorage.setItem(V4_SCOPE_KEY, JSON.stringify({
    accountId,
    accountName,
    manager: !!manager,
    trail: Array.isArray(trail) ? trail : [],
  }));
}

export function clearV4SessionScope() {
  localStorage.removeItem(V4_SCOPE_KEY);
}

const MAX_SCOPE_TRAIL = 20;

// This tab's scope (null = own, undefined = not booted); the stored one is shared by every tab.
let v4BootScope;

export function setV4BootScope(scope) {
  v4BootScope = scope === undefined ? undefined : scope || null;
}

export function getV4BootScope() {
  return v4BootScope;
}

const tabScope = () => (v4BootScope !== undefined ? v4BootScope : getV4SessionScope());

const sameAccount = (a, b) => !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();

// Re-entering the current scope doesn't push it, or "back" would be a no-op.
export function enterV4SessionScope(accountId, accountName, {manager = false} = {}) {
  const current = tabScope();
  const trail = Array.isArray(current?.trail) ? [...current.trail] : [];
  const reentered = sameAccount(current?.accountId, accountId) && !!current.manager === !!manager;
  if (current?.accountId && !reentered) {
    trail.push({accountId: current.accountId, accountName: current.accountName, manager: !!current.manager});
  }
  setV4SessionScope(accountId, accountName, {manager, trail: trail.slice(-MAX_SCOPE_TRAIL)});
}

export function leaveV4SessionScope() {
  const current = tabScope();
  const trail = Array.isArray(current?.trail) ? current.trail : [];
  const previous = trail[trail.length - 1];
  if (!previous?.accountId) {
    clearV4SessionScope();
    return null;
  }
  setV4SessionScope(previous.accountId, previous.accountName, {
    manager: previous.manager,
    trail: trail.slice(0, -1),
  });
  return getV4SessionScope();
}

export const isV4ManagerScope = scope => !!scope?.accountId && scope.manager === true;

/**
 * The account the signed-in V4 user belongs to (`owner_id` from GET /v1/user) —
 * the scope the request interceptor falls back to when no sub-account is entered.
 * In memory only: the V4-native boot re-reads it on every page load and every
 * session change reloads, so persisting it would only let one user's account
 * outlive its token. A V3 session leaves it null and uses getV4AccountUuid.
 */
let v4OwnerAccount = null;

export function setV4OwnerAccount(accountId) {
  v4OwnerAccount = accountId || null;
}

export function getV4OwnerAccount() {
  return v4OwnerAccount;
}

export function clearV4Session() {
  localStorage.removeItem(V4_USER_KEY);
  sessionStorage.removeItem(V4_USER_KEY);
  clearV4SessionScope();
  v4OwnerAccount = null;
}

/**
 * Half the boot test — the caller adds "no restored V3 user". Together they
 * mirror tryLoginV4Native's own early returns.
 */
export function canBootV4Native() {
  return !!getV4Token() && !isOnV4ExcludedPath();
}
